import { useState, useEffect, useRef, useReducer, useMemo } from 'react';
import { useAudioRecorderGeneral } from '../../hooks/useAudioRecorderGeneral';
import { fetchGame, sendGameScoresToServer } from '../../api/fetchGame';
import { handleError } from '../../context/errors/errorUtils';
import { useAppConfig } from '../../context/AppConfigContext';
import { useAssistant } from '../../components/Assistant/AssistantContext'; 
import gameReducer from './gameReducer';
import styles from './Game.module.css';
import common from '../Lessons/Common.module.css';
import FireCanvas from './FireCanvas';
import FallingWord from './FallingWord';
import GameStatsTabs from './GameStatsTabs';
import GameShareScreen from './GameShareScreen';
import { FaHeart } from 'react-icons/fa'; // для сердечек
import { getTranslations } from '../../utils/appLanguage';
import { Capacitor } from '@capacitor/core';
import waitGif from '../wait100.gif';

import AudioTimeEnded from '../../components/AudioTimeEnded/AudioTimeEnded';

const WATER_THEME_TRANSLATIONS = {
  ru: {
    assistant: {
      game: {
        intro: 'Помогите, я тону! Переводи слова в каплях и помоги мне выбраться на сушу.',
        correct: [
          'Да, вода отступает!',
          'Отлично, ещё немного и я выберусь.',
          'Верно, держи темп.'
        ],
        wrong: [
          'Нет, я снова ухожу под воду.',
          'Промах, волна поднимается.',
          'Нужно точнее, вода прибывает.'
        ],
        lowLives: [
          'Осторожно, я почти под водой.',
          'Ещё немного ошибок и будет совсем тяжело.',
          'Соберись, мне нужна твоя помощь.'
        ],
        fireOut: 'Вода ушла, я в безопасности. Ты мой герой!',
        criticalLives: [
          'Это последний шанс, держись.',
          'Сейчас особенно важно ответить точно.'
        ],
        highFire: [
          'Вода поднимается слишком быстро!',
          'Меня почти накрыло, скорее.',
          'Ещё немного и волны станут слишком высокими.'
        ]
      }
    },
    game: {
      fireSaved: 'Ассистент выбрался из воды!',
      victory: 'Победа! Ассистент спасён из воды!',
      defeat: 'Игра окончена. Ассистент не успел выбраться из воды.'
    }
  },
  en: {
    assistant: {
      game: {
        intro: 'Help, I am sinking! Translate the words in the drops and get me back to shore.',
        correct: [
          'Yes, the water is going down!',
          'Great, just a little more.',
          'Correct, keep it going.'
        ],
        wrong: [
          'No, I am slipping deeper.',
          'Missed it, the water is rising.',
          'We need a more accurate answer.'
        ],
        lowLives: [
          'Careful, I am almost underwater.',
          'A few more mistakes and this gets rough.',
          'Focus, I need your help.'
        ],
        fireOut: 'The water is gone, I am safe. You are my hero!',
        criticalLives: [
          'This is the last chance, stay sharp.',
          'Accuracy matters most right now.'
        ],
        highFire: [
          'The water is rising too fast!',
          'I am almost under, hurry up.',
          'One more mistake and the waves get too high.'
        ]
      }
    },
    game: {
      fireSaved: 'The assistant made it out of the water!',
      victory: 'Victory! The assistant was rescued from the water!',
      defeat: 'Game over. The assistant did not make it out of the water.'
    }
  },
  de: {
    assistant: {
      game: {
        intro: 'Hilfe, ich gehe unter! Uebersetze die Woerter in den Tropfen und bring mich ans Ufer.',
        correct: [
          'Ja, das Wasser geht zurueck!',
          'Super, nur noch ein bisschen.',
          'Richtig, genau so weiter.'
        ],
        wrong: [
          'Nein, ich rutsche tiefer ins Wasser.',
          'Daneben, das Wasser steigt.',
          'Wir brauchen eine genauere Antwort.'
        ],
        lowLives: [
          'Vorsicht, ich bin fast unter Wasser.',
          'Noch ein paar Fehler und es wird kritisch.',
          'Konzentrier dich, ich brauche deine Hilfe.'
        ],
        fireOut: 'Das Wasser ist weg, ich bin in Sicherheit. Du bist mein Held!',
        criticalLives: [
          'Das ist die letzte Chance, bleib konzentriert.',
          'Jetzt zaehlt jede richtige Antwort.'
        ],
        highFire: [
          'Das Wasser steigt viel zu schnell!',
          'Ich bin fast weggerissen, beeil dich.',
          'Noch ein Fehler und die Wellen werden zu hoch.'
        ]
      }
    },
    game: {
      fireSaved: 'Der Assistent ist aus dem Wasser raus!',
      victory: 'Sieg! Der Assistent wurde aus dem Wasser gerettet!',
      defeat: 'Spiel vorbei. Der Assistent hat es nicht aus dem Wasser geschafft.'
    }
  },
  uk: {
    assistant: {
      game: {
        intro: 'Рятуйте, я тону! Перекладай слова в краплях і допоможи мені вибратися на берег.',
        correct: [
          'Так, вода відступає!',
          'Чудово, ще трохи і я виберуся.',
          'Правильно, тримай темп.'
        ],
        wrong: [
          'Ні, я знову йду під воду.',
          'Промах, вода піднімається.',
          'Треба точніше, хвиля прибуває.'
        ],
        lowLives: [
          'Обережно, я майже під водою.',
          'Ще трохи помилок і стане зовсім важко.',
          'Зосередься, мені потрібна твоя допомога.'
        ],
        fireOut: 'Вода зійшла, я у безпеці. Ти мій герой!',
        criticalLives: [
          'Це останній шанс, тримайся.',
          'Зараз особливо важливо відповісти точно.'
        ],
        highFire: [
          'Вода піднімається занадто швидко!',
          'Мене майже накрило, швидше.',
          'Ще трохи і хвилі стануть надто високими.'
        ]
      }
    },
    game: {
      fireSaved: 'Асистент вибрався з води!',
      victory: 'Перемога! Асистента врятовано з води!',
      defeat: 'Гру завершено. Асистент не встиг вибратися з води.'
    }
  }
};

const normalizeGameLang = (lang) => (lang === 'ua' ? 'uk' : lang);

const getGameTranslations = (lang, theme) => {
  const normalizedLang = normalizeGameLang(lang) || 'en';
  const baseTranslations = getTranslations(normalizedLang) || getTranslations('en');

  if (theme !== 'water') {
    return baseTranslations;
  }

  const waterTranslations =
    WATER_THEME_TRANSLATIONS[normalizedLang] || WATER_THEME_TRANSLATIONS.en;

  return {
    ...baseTranslations,
    assistant: {
      ...baseTranslations?.assistant,
      game: {
        ...baseTranslations?.assistant?.game,
        ...waterTranslations?.assistant?.game
      }
    },
    game: {
      ...baseTranslations?.game,
      ...waterTranslations?.game
    }
  };
};

const Game = ({
  userId,
  gameLevel,
  allPhrasesCount,
  onExit,
  onGoPremium = null,
  fetchGameFn = fetchGame,
  sendScoresFn = sendGameScoresToServer,
  recordSource = 'game',
  fixedDifficulty = null,
  pathStepId = null,
  stepGoalPhrases = null
}) => { // allPhrasesCount - количество фраз произнесённых человеком. От них зависит язык реплик помощника base или target
  const buttonRef = useRef(null);
  const { actions, state: assistantState } = useAssistant();
  const { isTelegramWebApp, t, baseLanguage, targetLanguage, isAndroidApp, site, language } = useAppConfig();
  const [sentences, setSentences] = useState([]);
  const [gameOver, setGameOver] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [introData, setIntroData] = useState([]);
  const [introPlayed, setIntroPlayed] = useState(false);
  const [gameId, setGameId] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTranslation, setShowTranslation] = useState(false);
  const [resultHandled, setResultHandled] = useState(false);
  const [fallingWordProcessingStarted, setFallingWordProcessingStarted] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const isNative = Capacitor.isNativePlatform();
  const combinedIsAndroidApp = isAndroidApp || isNative;  
  const [manualRecordingStarted, setManualRecordingStarted] = useState(false);
  
  // stepId - количество всего фраз (правильные + неправильные)
  // phrases - количество фраз для показа потом в App
  // round - текущий раунд игры, соответствует количеству правильных ответов. 
  // phrasesCount - количество произнесённых фраз (обнуляется при распознавании с сервера)
  
  const [correctStreak, setCorrectStreak] = useState(0);
  const [difficulty, setDifficulty] = useState(fixedDifficulty || null); // указываем сложность игры, от неё потом будут зависеть параметры: скорость падения; количество очков; количество правильных ответов для сердечка; разное отображение подсказок ====== возможно, что-то ещё
  const gameTheme = difficulty === 'medium' ? 'water' : 'fire';
  const [score, setScore] = useState(0);
  const [round, setRound] = useState(1); 
  const [phrases, setPhrases] = useState(0);  // эти фразы используются для визуализации зачисления, а phrasesCount используются для сохранения результата на сервере

  const [fireOut, setFireOut] = useState(false);
  const [statsByPeriod, setStatsByPeriod] = useState(false);
  const [statsLoaded, setStatsLoaded] = useState(false);
  
  const [showRescueDialog, setShowRescueDialog] = useState(false);   // rescue dialog
  const [showStepGoalDialog, setShowStepGoalDialog] = useState(false);
  const [stepGoalReached, setStepGoalReached] = useState(false);
  
  const [gameStartTime, setGameStartTime] = useState(null);
  const [phraseTimes, setPhraseTimes] = useState([]); // массив времени для каждой фразы
  const [showShare, setShowShare] = useState(false);  
  const normalizedStepGoalPhrases = Math.max(Number(stepGoalPhrases) || 0, 0);
  const currentCorrectPhrases = Math.max((Number(round) || 1) - 1, 0);
  // Игра заканчивается если:
  // 1. Проиграл (нет сердечек или высокий уровень огня). Они как-то глупо зависят друг от друга. Надо подумать!
  // 2. Спас асистента (погасил огонь), но можно продолжить!
  // 3. Закончились слова - Выиграл. 
  // 4. Если ассистент спасён - всегда выигрыш

  const [state, dispatch] = useReducer(gameReducer, {
    recognizedText: '',
    audioBlob: null,
    currentSentenceIndex: 0,
    correctAnswer: null,
    fireLevel: 10, // Уровень огня от 0 (погас) до 20 (максимум)
    lives: 10, // Количество жизней
    wordPosition: 0, // Позиция падающего слова (0 - верх, 100 - низ)
	phrasesCount: 0,
	audioTimeEnded: false,
  });
  
  const { currentSentenceIndex, recognizedText, audioBlob, correctAnswer, fireLevel, lives, wordPosition, phrasesCount, audioTimeEnded } = state;
  
  // Загружаем фразы с сервера (сразу загружается 100 фраз):
	useEffect(() => {
	  const loadGame = async () => {
		setLoading(true);
		await fetchGameFn({
      userId,
      setData: setSentences,
      setGameId,
      setLoading,
      setError,
      gameLevel,
      stepId: pathStepId,
      difficulty: fixedDifficulty || difficulty
    });
	  };
	  loadGame();
	}, []);

  useEffect(() => {
    // Path/check mode has fixed difficulty, so we bypass difficulty selector
    // and need to unlock falling cards immediately.
    if (fixedDifficulty && !introPlayed) {
      setDifficulty(fixedDifficulty);
      setIntroPlayed(true);
      setGameStartTime(Date.now());
    }
  }, [fixedDifficulty, introPlayed]);

  // ======== разное отображение в зависимости от сложности игры =============
  const generateSentenceDisplay = (translation, difficulty) => {
    if (difficulty === 'easy') {
      return translation
        .split(' ')
        .map(word => word.length >= 3 ? `${word[0]}${'_'.repeat(word.length - 1)}` : '_'.repeat(word.length))
        .join(' ');
    }
  
    if (difficulty === 'medium') {
      return translation
        .split(' ')
        .map(word => '_'.repeat(word.length))
        .join(' ');
    }
    return null; // hard — ничего не показываем
  };

  // Логика обновления correctStreak при правильном ответе
  const updateCorrectStreak = () => {
    setCorrectStreak(prev => {
      const newStreak = prev + 1;
      const streakGoal = difficulty === 'easy' ? 3 : difficulty === 'medium' ? 5 : 7;
      if (newStreak >= streakGoal) {
        dispatch({ type: 'INCREMENT_LIVES' });
        return 0;
      }
      return newStreak;
    });
  };  

  const currentSentence = sentences[currentSentenceIndex];

  // Обработка результата распознавания
  useEffect(() => {
    if (resultHandled) return;
	if (correctAnswer === null) return;
	
	dispatch({ type: 'INCREMENT_PHRASES_COUNT' });
	setPhrases((prev) => prev + 1);
	
    if (correctAnswer === true) {
      const wordCount = currentSentence?.text?.trim().split(/\s+/).length || 0;
	  setScore((prev) => prev + 2 * wordCount); // тут можно менять в зависимости от сложности
	  setRound((prev) => prev + 1);
      updateCorrectStreak();
	  handleCorrectAnswer();
      setResultHandled(true); // <— предотвращает повторную обработку
    } 
    if (correctAnswer === false) {
      handleIncorrectAnswer();
      setResultHandled(true); 
    }
  }, [correctAnswer]);

	const getCurrentLanguage = (allPhrasesCount, baseLanguage, targetLanguage) => {
	  return normalizeGameLang(allPhrasesCount < 2000 ? baseLanguage : targetLanguage);
	};

  const assistantLanguage = getCurrentLanguage(allPhrasesCount, baseLanguage, targetLanguage);
  const normalizedBaseLanguage = normalizeGameLang(baseLanguage);
  const canUseGameShareScreen = ['ru', 'uk'].includes(normalizedBaseLanguage);
  const assistantGameTranslations = useMemo(
    () => getGameTranslations(assistantLanguage, gameTheme),
    [assistantLanguage, gameTheme]
  );
  const uiGameTexts = useMemo(
    () => getGameTranslations(language, gameTheme)?.game || t.game,
    [language, gameTheme, t]
  );

	const getLocalizedPhrasePool = (type, _allPhrasesCount, _baseLanguage, _targetLanguage) => {
	  return assistantGameTranslations?.assistant?.game?.[type];
	};  

	// выбор уровня сложности 
	const handleSetDifficulty = (level) => {
	  setDifficulty(level);
	  actions.cancelTyping();
	  setTimeout(() => {
		  sayWithPause(getLocalizedPhrasePool("intro", allPhrasesCount, baseLanguage, targetLanguage)); // интро ассистента
		  setGameStartTime(Date.now()); // <— начинаем отсчет времени игры
	  }, 500);
	  setTimeout(() => {
		  setIntroPlayed(true);
	  }, 500);
	}; 

	// ================= блок работы ассистента ================================================
	const sayWithPause = async (text) => {
	  actions.resetState();	
	  setIsPaused(true);
	  actions.speakText(text); 
	  setTimeout(() => {
		setIsPaused(false);
	  }, 4000);
	};
	const usedPhrases = new Set(); // можно ввести в состояние, если нужно reset на уровне игры
	
	const maybeSayAssistantPhrase = async (type, context = {}) => {
	  
	  let pool = getLocalizedPhrasePool(type, allPhrasesCount, baseLanguage, targetLanguage);
	  if (context.fireLevel === 0 && !fireOut) {
		const phrase = getLocalizedPhrasePool("fireOut", allPhrasesCount, baseLanguage, targetLanguage);
		await sayWithPause(phrase);
		setFireOut(true);
		setShowRescueDialog(true); // Показываем диалог и ставим игру на паузу
		return;
	  }
	  
	  if (Math.random() > 0.25) { // С вероятностью 25% ассистент говорит
		if (type === 'correct') { actions.showSuccess(); }
		else if (type === 'wrong') { actions.showError(); }
		else if (type === 'lowLives') { actions.showSurprise(); }			
		return;   
	  }
	  if (type === 'lowLives' && context.lives === 1) {
		pool = getLocalizedPhrasePool("criticalLives", allPhrasesCount, baseLanguage, targetLanguage);
	  } else if (type === 'wrong' && context.fireLevel >= 15) {
		pool = getLocalizedPhrasePool("highFire", allPhrasesCount, baseLanguage, targetLanguage);
	  }

	  if (!Array.isArray(pool) || pool.length === 0) {
		return;
	  }

	  // Исключить уже сказанные фразы, если хочется разнообразия
	  const available = pool.filter(p => !usedPhrases.has(p));
	  const phrase = available.length > 0
		? available[Math.floor(Math.random() * available.length)]
		: pool[Math.floor(Math.random() * pool.length)];

	  usedPhrases.add(phrase);
	  await sayWithPause(phrase);
	};
	
	// обработка правильного/неправильного ответа 
	const handleCorrectAnswer = () => {
	  setShowTranslation(true);
	  setManualRecordingStarted(false);
	};

	const handleIncorrectAnswer = () => {
	  setCorrectStreak(0);
	  setShowTranslation(true);
	  setManualRecordingStarted(false);
	};


	// 🧠 Обработка ситуации, когда пользователь молчит - обрабатывается через dispatch в FallingWord

  // Переход к следующей фразе
  const goToNextSentence = () => {
    const nextSentenceIndex = currentSentenceIndex + 1;
	if (nextSentenceIndex < sentences.length) {
      dispatch({ type: 'SET_CURRENT_SENTENCE', payload: nextSentenceIndex });
      setResultHandled(false); 
	  setPhraseTimes(prev => [...prev, { start: Date.now(), end: null }]);
    } else {
	  setGameWon(true);	
	  setGameOver(true); // ⛔ фразы закончились	
    }
  };
     
  // Проверка условий поражения. Победа проверяется в других местах
  useEffect(() => {
	if (lives === 0 || fireLevel === 20) {
      setGameOver(true);
    }
  }, [fireLevel, lives]);  

  useEffect(() => {
    if (recordSource !== 'pathgame') return;
    if (normalizedStepGoalPhrases <= 0) return;
    if (stepGoalReached || showStepGoalDialog) return;
    if (gameOver || gameWon || showRescueDialog) return;
    if (showTranslation) return;

    if (currentCorrectPhrases >= normalizedStepGoalPhrases) {
      setStepGoalReached(true);
      setIsPaused(true);
      setShowStepGoalDialog(true);
    }
  }, [
    recordSource,
    normalizedStepGoalPhrases,
    currentCorrectPhrases,
    showTranslation,
    showRescueDialog,
    stepGoalReached,
    showStepGoalDialog,
    gameOver,
    gameWon,
  ]);

  const stepId = currentSentence?.id + 1 || currentSentenceIndex + 1;
  const step = {
    id: stepId,
    gameId,
    pathStepId,
    options: currentSentence ? [{ userText: currentSentence.translation }] : [],
	phrasesCount
  };

  // Инициализация хука записи
  const recorder = useAudioRecorderGeneral({
    userId,
    stepId,
    step,
    contextId: gameId,
    source: recordSource,
    dispatch,
    state,
    buttonRef,
    isTelegramWebApp,
    completeBlock: false,
  });


	
  // конец игры: Можно выбирать конец, когда спасли ассистента. 
	useEffect(() => {
	  if (gameOver || gameWon) {
		const gameEndTime = Date.now();
		const gameDuration = gameStartTime ? (gameEndTime - gameStartTime) / 1000 : 0; // в секундах

		const phraseDurations = phraseTimes
		  .filter(p => p.start && p.end)
		  .map(p => (p.end - p.start) / 1000); // в секундах

		const averagePhraseTime = phraseDurations.length > 0
		  ? phraseDurations.reduce((a,b) => a + b, 0) / phraseDurations.length
		  : 0;

		// Отправляем метрики в GameStatsTabs или сервер
		sendScoresFn({
		  userId,
		  gameId,
		  stepId,
      pathStepId,
		  score,
		  phrasesCount: phrases,
		  round,
		  difficulty,
		  gameDuration,
		  averagePhraseTime
		}).then(data => {
		  if (data?.stats?.leaderboardByPeriod) {
			setStatsByPeriod(data.stats.leaderboardByPeriod);
		  }
		  setStatsLoaded(true);
		});
	  }
	}, [gameOver, gameWon]);

	// Проверка обязательных данных
  useEffect(() => {
    if (!gameId) {
      handleError({
        code: 'MISSING_GAME_ID',
        error: new Error("MISSING_GAME_ID"),
        context: { module: 'Game', gameId: gameId },
        showToUser: true,
      });
    }
  }, [gameId, t]);
  
	const speedMultiplier = {
	  easy: 0.5,
	  medium: 0.55,
	  hard: 0.55,
	};

	const baseSpeed = 0.02 + currentSentenceIndex * 0.0005;
	//const baseSpeed = 0.1 + currentSentenceIndex * 0.001; // высокая скорость для теста
	const adjustedSpeed = Math.min(baseSpeed * (speedMultiplier[difficulty] || 1), 0.3);
  
	if (loading) { return ( <div style={{
		  display: 'flex',
		  justifyContent: 'center',
		  alignItems: 'flex-start',
		  height: '100vh',
		  paddingTop: '100px'
		}}><img src={waitGif} alt="Loading..." /></div>);
	}

  if (!recorder.isSupported) {
    return <div className={styles.error}>{t.translateBlock.audio_not_supported}</div>;
  }
  
  if (audioTimeEnded) { return <AudioTimeEnded onBack={() => { 
		dispatch({ type: 'RESET_AUDIO_TIME_ENDED' });
		setGameOver(true);
	}} onGoPremium={onGoPremium} />; }  
  
  
  if (!difficulty) {
    return (
      <div className={styles.difficultySelector}>
        <h2>{t.game.chooseDifficulty}</h2>
        <button onClick={() => handleSetDifficulty('easy')}>{t.game.easy}</button>
        <button onClick={() => handleSetDifficulty('medium')}>{t.game.medium}</button>
        <button onClick={() => handleSetDifficulty('hard')}>{t.game.hard}</button>
      </div>
    );
  }
  
  return (
    <div className={`${styles.gameContainer} ${gameTheme === 'water' ? styles.gameContainerWater : styles.gameContainerFire}`}>
      {currentSentence && !gameOver && !gameWon && !isPaused && !showRescueDialog && !showStepGoalDialog && introPlayed && (
        <FallingWord
          key={currentSentenceIndex}
          currentSentenceIndex={currentSentenceIndex}
          isProcessing={manualRecordingStarted}
          speed={adjustedSpeed}
          text={currentSentence.text}
          audio={`${combinedIsAndroidApp ? site : ''}${currentSentence.audio}`}
          translation={currentSentence.translation}
          tips = {generateSentenceDisplay(currentSentence.translation, difficulty)}
          correctAnswer={correctAnswer}
          showTranslation={showTranslation}
          theme={gameTheme}
          dispatch={dispatch} 
          onProcessingStart={(value) => setFallingWordProcessingStarted(value)}
		  onTranslationComplete={async () => {
			setShowTranslation(false);
			dispatch({ type: 'SET_WORD_POSITION', payload: 0 });
			if (correctAnswer === true) {
			  const nextFireLevel = Math.max(0, fireLevel - 1);
			  dispatch({ type: 'UPDATE_FIRE_LEVEL', payload: -1 });
			  await maybeSayAssistantPhrase('correct', { fireLevel: nextFireLevel, lives });
			} else {
			  const nextFireLevel = Math.min(20, fireLevel + 1);
			  const nextLives = Math.max(0, lives - 1);
			  dispatch({ type: 'UPDATE_FIRE_LEVEL', payload: 1 });
			  dispatch({ type: 'DECREMENT_LIVES' });
			  await maybeSayAssistantPhrase(nextLives < 3 ? 'lowLives' : 'wrong', {
				fireLevel: nextFireLevel,
				lives: nextLives
			  });
			}
			goToNextSentence();
		  }}
        />
      )}  
		{/*закончить игру*/}
		<div className={styles.exitButton}
		  onClick={() => { setGameOver(true);}}
		  title="End Game"
		>{phrases}</div>
		
		{/*сердечки*/}
		{!gameOver && !gameWon && (
		  <div className={styles.livesColumn}>
			{Array.from({ length: lives }, (_, i) => (
			  <FaHeart key={i} color="red" size={24} />
			))}
		  </div>
		)}

		{!audioBlob && !gameOver && !gameWon && (
		  <div className={`${styles.recorderContainer} ${gameTheme === 'water' ? styles.recorderContainerWater : ''}`}>
			<button
			  ref={buttonRef}
			  type="button"
			  className={`${styles.recordButton} ${recorder.isRecording ? styles.recording : ''}`}
			  disabled={!recorder.permissionGranted || recorder.isProcessing || fallingWordProcessingStarted }
			  onMouseDown={(e) => {
				  e.preventDefault();
				  recorder.handleStartRecording(currentSentence?.options || []);
				  setManualRecordingStarted(true);
				  setPhraseTimes(prev => {
					const updated = [...prev];
					const last = updated[updated.length - 1];
					if (last && !last.end) last.end = Date.now(); // фиксируем конец для последней фразы
					return updated;
				  });
				}}
			  onTouchStart={(e) => {
				  e.preventDefault();
				  recorder.handleStartRecording(currentSentence?.options || []);
				  setManualRecordingStarted(true);
				  setPhraseTimes(prev => {
					const updated = [...prev];
					const last = updated[updated.length - 1];
					if (last && !last.end) last.end = Date.now(); // фиксируем конец для последней фразы
					return updated;
				  });
				}}
			  onMouseUp={(e) => {
				  e.preventDefault();
				  recorder.handleStopRecording();
				}}
			  onMouseLeave={(e) => {
				  e.preventDefault();
				  recorder.handleStopRecording();
				}}
			  onTouchEnd={(e) => {
				  e.preventDefault();
				  recorder.handleStopRecording();
				}}
			  onTouchCancel={(e) => {
				  e.preventDefault();
				  recorder.handleStopRecording();
				}}
			>
			  <svg className={styles.recordIcon} viewBox="0 0 24 24">
				<path fill="white" d="M12 14a3 3 0 0 0 3-3V5a3 3 0 0 0-6 0v6a3 3 0 0 0 3 3zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 14 0h-2zM11 21h2v-2h-2v2z"/>
			  </svg>
			</button>
		  </div>
		)}

		{phrases >= 1 && phrases <= 3 && allPhrasesCount < 1000 && !gameOver && !gameWon && (
			<div className={styles.endGameHint}>
				<p>{t.game.endGameHint || 'Нажмите, чтобы закончить игру'}</p>
			</div>
		)}

      {fireLevel > 0 && (
        <div className={styles.fireContainer}>
          <FireCanvas fireLevel={fireLevel} theme={gameTheme} />
        </div>
      )}

		{fireLevel === 0 && !gameOver && fireOut && showRescueDialog && gameTheme === 'water' && (
		  <div className={styles.victoryDialog}>
			<p>{uiGameTexts.fireSaved} 💧🙌</p>
			<button onClick={() => { setGameOver(true); setShowRescueDialog(false); }}>{t.game.complete}</button>
			<button onClick={() => { setFireOut(true);  setShowRescueDialog(false); }}>{t.game.continue}</button>
		  </div>
		)}
	  
		{fireLevel === 0 && !gameOver && fireOut && showRescueDialog && gameTheme !== 'water' && (
		  <div className={styles.victoryDialog}>
			<p>{t.game.fireSaved} 🔥👏</p>
			<button onClick={() => { setGameOver(true); setShowRescueDialog(false); }}>{t.game.complete}</button>
			<button onClick={() => { setFireOut(true);  setShowRescueDialog(false); }}>{t.game.continue}</button>
		  </div>
		)}

		{!gameOver && !gameWon && showStepGoalDialog && (
		  <div className={styles.victoryDialog}>
			<p>{t.game?.stepGoalReached || t.game?.endGameHint || 'You reached the phrase goal for this circle.'}</p>
			<button onClick={() => { setShowStepGoalDialog(false); setGameOver(true); setIsPaused(false); }}>{t.game.complete}</button>
			<button onClick={() => { setShowStepGoalDialog(false); setIsPaused(false); }}>{t.game.continue}</button>
		  </div>
		)}
		{(gameOver || gameWon) && statsLoaded && (
			<div className={styles.gameOver}>
			{!showShare || !canUseGameShareScreen ? (
			  <>
				<p className={styles.gameOverText}>
				  {fireOut ? uiGameTexts.victory : uiGameTexts.defeat}
				</p>
				{statsByPeriod ? (
				  <GameStatsTabs stats={statsByPeriod} />
				) : (
				  <p>Statistics not available</p>
				)}
				<div className={styles.centeredButton}>
				  <button
					className={styles.completeButton}
					onClick={() => {
					  if (canUseGameShareScreen) {
						setShowShare(true);
					  } else {
						onExit(score, phrases);
					  }
					}}
				  >
					Ok
				  </button>
				</div>
			  </>
			) : (
			  <>
				<GameShareScreen score={score} phrases={phrases} gameStartTime={gameStartTime} />
				<div className={styles.centeredButton}>
				  <button className={styles.completeButton} onClick={() => onExit(score, phrases)}>Ok</button>
				</div>
			  </>
			)}
			</div>
		)}


    </div>
  );
};

export default Game;

/*
					onClick={() => {
					  if (combinedIsAndroidApp) {
						setShowShare(true);
					  } else {
						onExit(score, phrases);
					  }
					}}

*/



